// Import Swiper styles
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/navigation";
import "swiper/css/scrollbar";

import "../../../theme/SwiperOverrides.css";

import { A11y, Navigation, Scrollbar, Virtual } from "swiper/modules";

import React, { lazy, useCallback, useEffect, useState } from "react";
import { useMemo } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import DogCarouselCard, {
	type DogCarouselCardProps,
} from "../../../components/SignPostCards/DogCarouselCard";
import PlaceholderDogCarouselCard from "../../../components/SignPostCards/PlaceholderDogCarouselCard/PlaceholderDogCarouselCard";
import { ArrowCircle } from "../../../components/common/Icons";
import { useDogSearchApi } from "../../../context";
import { useClientSide } from "../../../hooks/useClientSide";
import useWindowWidth from "../../../hooks/useWindowWidth";
import { Colours } from "../../../utils/colours";
import WrappedSection from "../../WrappedSection/WrappedSection";

import SSRSuspense from "../../../components/SSRSuspense/SSRSuspense";
import type { DogSearchProps } from "../../filters/DogSearch/uk/DogSearch";
import * as styles from "./SectionDogSearchSuggestions.module.scss";

const DogSearch: React.FC<DogSearchProps> =
	process.env.GATSBY_IS_IE === "true"
		? lazy(() => import("../../filters/DogSearch/ie/DogSearch"))
		: lazy(() => import("../../filters/DogSearch/uk/DogSearch"));

export const SectionDogSearchSuggestions: React.FC = () => {
	const { results, useDogSearchQuery } = useDogSearchApi();
	const isClientSide = useClientSide();
	const windowWidth = useWindowWidth();
	const [swiper, setSwiper] = useState(null);
	const [currentSlide, setCurrentSlide] = useState(0);
	// biome-ignore lint/correctness/useExhaustiveDependencies: We only want this to run once
	useEffect(() => {
		useDogSearchQuery();
	}, []);

	const items = useMemo(() => {
		let itemsToReturn: DogCarouselCardProps[] = [];
		const minItems = 16;

		if (itemsToReturn.length < minItems && results?.length > 0) {
			itemsToReturn = [
				...itemsToReturn,
				...results.map((dog) => ({ dog })),
			].slice(0, minItems - itemsToReturn.length);
		}
		return itemsToReturn.sort((a) =>
			a.dog?.media?.images?.[0]?.src.length > 0 ? -1 : 0,
		);
	}, [results]);
	const getVisibleCardCount = useCallback((wW: number) => {
		if (wW >= 1200) return 4;
		if (wW >= 1024) return 3;
		return 1;
	}, []);

	const [nextButtonDisabled, setNextButtonDisabled] = useState(
		swiper?.activeIndex >= items.length - getVisibleCardCount(windowWidth),
	);

	const prevSlide = useCallback(() => {
		swiper?.activeIndex > 0 && swiper?.slidePrev();
		setNextButtonDisabled(
			swiper?.activeIndex >= items.length - getVisibleCardCount(windowWidth),
		);
	}, [swiper, items.length, windowWidth, getVisibleCardCount]);

	const nextSlide = useCallback(() => {
		swiper?.activeIndex < items.length - 2 && swiper?.slideNext();
		setNextButtonDisabled(
			swiper?.activeIndex >= items.length - getVisibleCardCount(windowWidth),
		);
	}, [swiper, items.length, windowWidth, getVisibleCardCount]);

	useEffect(() => {
		setNextButtonDisabled(
			swiper?.activeIndex >= items.length - getVisibleCardCount(windowWidth),
		);
	}, [windowWidth, items.length, swiper, getVisibleCardCount]);

	return (
		<WrappedSection
			sectionName="SectionDogSearchSuggestions"
			sectionColour={Colours.SANDY_FUR}
		>
			<div className={styles.dogsearchsuggestionsinner}>
				<div className={styles.dogfiltercontainer}>
					<SSRSuspense>
						<DogSearch stackInputs isHomepage />
					</SSRSuspense>
				</div>
				<div className={styles.dogresultscontainer}>
					{isClientSide && (
						<Swiper
							key={items.length}
							className={styles.swiper}
							modules={[Virtual, Navigation, A11y, Scrollbar]}
							edgeSwipeThreshold={10}
							threshold={0}
							grabCursor={true}
							draggable={false}
							resistance={false}
							autoplay={false}
							noSwiping
							allowTouchMove={false}
							loop={false}
							onSwiper={(swiper) => setSwiper(swiper)}
							onSlideChange={(swiper) => setCurrentSlide(swiper.activeIndex)}
							initialSlide={0}
							breakpoints={{
								320: {
									slidesPerView: getVisibleCardCount(320),
									enabled: items.length > getVisibleCardCount(320),
								},
								620: {
									slidesPerView: getVisibleCardCount(620),
									enabled: items.length > getVisibleCardCount(620),
								},
								750: {
									slidesPerView: getVisibleCardCount(750),
									enabled: items.length > getVisibleCardCount(750),
								},
								1024: {
									slidesPerView: getVisibleCardCount(1024),
									enabled: items.length > getVisibleCardCount(1024),
								},
								1200: {
									slidesPerView: getVisibleCardCount(1200),
									enabled: items.length > getVisibleCardCount(1200),
								},
							}}
						>
							{items.length > 0
								? items.map((signPostCard) => (
										<SwiperSlide key={signPostCard.dog.id}>
											<DogCarouselCard {...signPostCard} />
										</SwiperSlide>
									))
								: [1, 2, 3, 4].map((x) => (
										<SwiperSlide key={x}>
											<PlaceholderDogCarouselCard />
										</SwiperSlide>
									))}
						</Swiper>
					)}
					<div className={styles.navbuttons}>
						<button
							type="button"
							aria-label="previous"
							className={styles.back}
							onClick={prevSlide}
							tabIndex={0}
							onKeyDown={(e) => {
								e.key === "Enter" && prevSlide();
							}}
							disabled={currentSlide === 0}
						>
							<ArrowCircle
								rotation={180}
								fill={
									currentSlide === 0
										? Colours.OATMEAL_LIGHT
										: Colours.SUNSHINE_YELLOW
								}
							/>
						</button>
						<button
							type="button"
							aria-label="next"
							className={styles.next}
							onClick={nextSlide}
							onKeyDown={(e) => {
								e.key === "Enter" && prevSlide();
							}}
							tabIndex={0}
							disabled={nextButtonDisabled}
						>
							<ArrowCircle
								rotation={0}
								fill={
									nextButtonDisabled
										? Colours.OATMEAL_LIGHT
										: Colours.SUNSHINE_YELLOW
								}
							/>
						</button>
					</div>
				</div>
			</div>
		</WrappedSection>
	);
};

export default SectionDogSearchSuggestions;
